import React from 'react';

const defaultProps = {
    element: 'div',
    useWindow: true,
    useCapture: false,
    initialLoad: false,
    parentElement: null,// the default is the parent element of the current element
}

function Scroll({
                    children,
                    element,
                    parentElement,
                    getRef,
                    useWindow,
                    useCapture,
                    initialLoad,
                    onScroll,
                    ...props
                }) {

    const scrollComponentRef = React.useRef(null);
    // A function that checks if the browser supports the `passive` event listener option
    const eventListenerOptions = React.useCallback(() => {
        const passiveSupported = (() => {
            let passive = false;
            const testOptions = {
                get passive() {
                    passive = true;
                },
            };
            try {
                document.addEventListener('test', null, testOptions);
                document.removeEventListener('test', null, testOptions);
            } catch (e) {
                // ignore
            }
            return passive;
        })();
        return {useCapture: useCapture, passive: passiveSupported ? true : false,};
    }, [useCapture]);

    // A function that listens to the scroll event and calls the `onScroll` callback
    const scrollListener = React.useCallback(() => {
        const element = scrollComponentRef.current;
        const scrollElement = useWindow ? window : getParentElement(element);
        const scrollTop = scrollElement.scrollTop || scrollElement.pageYOffset || 0;
        onScroll && onScroll(scrollTop);
    }, [onScroll, useWindow]);


    const getParentElement = React.useCallback((element) => {
        // return the parent element
        const scrollParent = parentElement && parentElement;
        if (scrollParent != null) {
            return scrollParent;
        }
        return element && element.parentNode;
    }, [parentElement]);

    // add scroll listener
    const attachScrollListener = React.useCallback(() => {
        const parent = getParentElement(scrollComponentRef.current);
        if (!parent) {
            return;
        }
        const scrollElement = useWindow ? window : parent;
        const options = eventListenerOptions();
        scrollElement.addEventListener('mousewheel', mousewheelListener, options);
        scrollElement.addEventListener('scroll', scrollListener, options);
        scrollElement.addEventListener('resize', scrollListener, options);
        return () => {
            scrollElement.removeEventListener('mousewheel', mousewheelListener, options);
            scrollElement.removeEventListener('scroll', scrollListener, options);
            scrollElement.removeEventListener('resize', scrollListener, options);
        };
    }, [getParentElement, eventListenerOptions, useWindow, scrollListener]);

    const detachMousewheelListener = React.useCallback(() => {
        const scrollElement = useWindow ? window : scrollComponentRef.current && scrollComponentRef.current.parentNode;
        const options = eventListenerOptions();
        scrollElement && scrollElement.removeEventListener('mousewheel', mousewheelListener, options);
    }, [eventListenerOptions, useWindow]);

    const mousewheelListener = React.useCallback((e) => {
        if (e.deltaY === 1 && !eventListenerOptions().passive) {
            e.preventDefault();
        }
    }, [eventListenerOptions]);

    React.useEffect(() => {
        const cleanup = attachScrollListener();
        return () => {
            cleanup && cleanup();
            detachMousewheelListener();
        };
    }, [attachScrollListener, detachMousewheelListener]);

    React.useEffect(() => {
        if (initialLoad) {
            scrollListener();
        }
    }, [initialLoad, scrollListener]);

    return React.createElement(element, {
        ...props,
        ref: (node) => {
            scrollComponentRef.current = node;
            getRef && getRef(node);
        },
    }, children);
}

Scroll.defaultProps = defaultProps;
export default Scroll;